import {
    Collection,
    DataMapper,
    IMappingArtifactSource,
    IModel,
    IParameterizable,
    MappingDirective,
    Metamodel,
    ModelConstants,
    Parameter,
    OwnedSet,
    Folder,
    Owned,
    Named,
    ModelMarker
} from 'shell-models-lib';
import { DataMapperMapping } from 'app/model/adfDataMapper/dataMapperMapping';
import { IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { MappingSourceConnectionInfo } from 'app/model/adfDataMapper/mappingSourceConnectionInfo';
import { MappingTargetConnectionInfo } from 'app/model/adfDataMapper/mappingTargetConnectionInfo';
import { MapperPolicy } from 'app/model/adfDataMapper/mapperPolicy';
import { MapperCompute } from 'app/model/adfDataMapper/mapperCompute';
import { MapperRunStatus } from 'app/model/adfDataMapper/mapperRunStatus';
import { MapperCadenceFrequency, MapperFrequencyMode, MapperPolicyRecurrence } from 'app/model/adfDataMapper/mapperPolicyRecurrence';
import { AdfDataMapperValidations } from 'app/model/adfDataMapper/adfDataMapper.validations';
import { MapperStagingSettings } from './mapperStagingSettings';

@Metamodel.model({
    name: ModelConstants.adfDataMapper,
    display: [stringResources.ADFDataMapperChangeDataCapturePreviewLabel, stringResources.ADFDataMapperChangeDataCapturePreviewLabel],
    reusable: true,
    referable: true,
    validator: AdfDataMapperValidations.validateAdfDataMapper

})
export class AdfDataMapper extends Named implements IParameterizable {
    public static readonly MODEL: IModel;
    public readonly useShellFolder = true;

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true
        }
    })
    public readonly parameters = OwnedSet.create<Parameter>(this);

    public folder: Folder = Owned.create(Folder.MODEL, this);

    @Metamodel.attribute({
        type: MappingSourceConnectionInfo.MODEL,
        collection: Collection.Array
    })
    public sourceConnectionsInfo: MappingSourceConnectionInfo[] = [];

    @Metamodel.attribute({
        type: MappingTargetConnectionInfo.MODEL,
        collection: Collection.Array
    })
    public targetConnectionsInfo: MappingTargetConnectionInfo[] = [];

    @Metamodel.attribute({
        type: MappingSourceConnectionInfo.MODEL,
        collection: Collection.Array
    })
    public nonCdcSourceConnectionsInfo: MappingSourceConnectionInfo[] = [];

    @Metamodel.attribute({
        type: MapperPolicy.MODEL
    })
    public policy: MapperPolicy = this._getDefaultPolicy();

    public runStatus?: string;

    public allowVNetOverride: boolean = false;

    public mapperId?: string;

    public compute: MapperCompute;

    public stagingSettings?: MapperStagingSettings;

    public isCdcTabFreshlyOpened: boolean = false;

    /**
     * Indicates that Start/Stop is initiated but the runstatus is not updated yet.
     */
    public isInInterimState: boolean = false;

    constructor(name?: string, description?: string) {
        super(name, description, null);
    }

    public getMapperRunStatus(): MapperRunStatus {
        if (!!this.runStatus) {
            if (this.runStatus === MapperRunStatus.Started) {
                return MapperRunStatus.Started;
            } else if (this.runStatus === MapperRunStatus.Stopped) {
                return MapperRunStatus.Stopped;
            } else if (this.runStatus === MapperRunStatus.Starting) {
                return MapperRunStatus.Starting;
            } else if (this.runStatus === MapperRunStatus.Stopping) {
                return MapperRunStatus.Stopping;
            } else if (this.runStatus === MapperRunStatus.Failed) {
                return MapperRunStatus.Failed;
            }
        }

        return MapperRunStatus.NotStarted;
    }

    public isPublished(): boolean {
        return this.runStatus !== undefined || !ModelMarker.isNewInPublish(this);
    }

    public toSynapseDataMapper(
        sourceConnectionInfo: MappingSourceConnectionInfo,
        targetConnectionInfo: MappingTargetConnectionInfo,
        selectedTargetTable?: string): DataMapper {

        const dataMappingModel: DataMapper = new DataMapper(this.name + 'synapse');
        dataMappingModel.sourceLinkedService = sourceConnectionInfo.connection.linkedService;
        dataMappingModel.targetLinkedService = targetConnectionInfo.connection.linkedService;

        const selectedSourceTables = !!selectedTargetTable? targetConnectionInfo.dataMapperMappings.filter((mapping)=>
            mapping.targetTable.tableName === selectedTargetTable).map((mapping)=> mapping.sourceTable) : sourceConnectionInfo.tables;

        const selectedTargetTables = !!selectedTargetTable?
        targetConnectionInfo.tables.filter((table)=> table.tableName === selectedTargetTable): targetConnectionInfo.tables;

        dataMappingModel.sourceDatasets = this._toIMappingArtifactSources(selectedSourceTables);
        dataMappingModel.targetDatasets = this._toIMappingArtifactSources(selectedTargetTables);
        dataMappingModel.mappingDirectives = this._toSynapseDataMapperMappingDirectives(sourceConnectionInfo, targetConnectionInfo);

        // filter mapping directives on the bases of selected target table
        if (!!selectedTargetTable) {
            dataMappingModel.mappingDirectives = dataMappingModel.mappingDirectives?.filter((mapping) => mapping.targetEntityReference.name === selectedTargetTable);
        }

        return dataMappingModel;
    }

    public isContinuous(): boolean {
        return this.policy.mode === MapperFrequencyMode.Realtime || (this.policy.recurrence.frequency === 'Minute' && this.policy.recurrence.interval === 1);
    }

    private _toIMappingArtifactSources(adfMapperTables: IMapperTable[]): IMappingArtifactSource[] {
        const synapseMapperTables: IMappingArtifactSource[] = [];
        if (adfMapperTables) {
            adfMapperTables.forEach((adfMapperTable) => synapseMapperTables.push(this._toIMappingArtifactSource(adfMapperTable)));
        }

        return synapseMapperTables;
    }

    private _toIMappingArtifactSource(adfMapperTable: IMapperTable): IMappingArtifactSource {
        return !!adfMapperTable ? { ...adfMapperTable, name: adfMapperTable.tableName} as unknown as IMappingArtifactSource : undefined;
    }

    private _toSynapseDataMapperMappingDirectives(
        sourceConnectionInfo: MappingSourceConnectionInfo,
        targetConnectionInfo: MappingTargetConnectionInfo): MappingDirective[] {
        const directives: MappingDirective[] = [];
        if (targetConnectionInfo && targetConnectionInfo.dataMapperMappings) {
            targetConnectionInfo.dataMapperMappings.forEach((dataMapperMapping) => {
                if (this._isCompatibleMapping(dataMapperMapping, sourceConnectionInfo)) {
                    directives.push(dataMapperMapping.toSynapseDataMapperMappingDirectives());
                }
            });
        }

        return directives;
    }

    private _isCompatibleMapping(
        dataMapperMapping: DataMapperMapping,
        sourceConnectionInfo: MappingSourceConnectionInfo): boolean {
        if (dataMapperMapping.sourceConnectionReference) {
            if (!sourceConnectionInfo.connection.isEqualToConnectionReference(dataMapperMapping.sourceConnectionReference)) {
                return false;
            }

            if (!!dataMapperMapping.sourceDenormalizeInfo && !!dataMapperMapping.sourceDenormalizeInfo.entities) {
                for (const entityReference of dataMapperMapping.sourceDenormalizeInfo.entities) {
                    if (!sourceConnectionInfo.connection.isEqualToConnectionReference(entityReference.toEntityConnectionReference)) {
                        return false;
                    }
                }
            }

            return true;
        }

        return false;
    }

    private _getDefaultPolicy(): MapperPolicy {
        const mapperPolicy = new MapperPolicy();
        mapperPolicy.mode = MapperFrequencyMode.Microbatch;
        mapperPolicy.recurrence = new MapperPolicyRecurrence(MapperCadenceFrequency.Minute, 15);

        return mapperPolicy;
    }
}
